import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TextField } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import * as lib from '~/dependencies';
import * as actions from '~/store/actions';
import messagesToast from '~/validations/messagesNotifications';
import { Container, BoxPostNote } from './styles';

function countWords(str) {
  if (!str) return 0;
  const words = str
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words.length;
}

export default function SendNotes({ callId, open, onClose, minWords }) {
  // STATES
  const [note, setNote] = useState();
  const [message, setMessage] = useState(false);
  const [error, setError] = useState(null);
  const [minWordsRequired, setMinWordsRequired] = useState(minWords);
  const [enableSubmitBtn, setEnableSubmitBtn] = useState(false);

  // REFs
  const timeout = useRef(0);

  // HOOKS
  const dispatch = useDispatch();
  const { t } = lib.useTranslation();

  // SELECTORS
  const draftsSaved = useSelector(state => state.draft?.data);
  const auth = useSelector(state => state.auth);

  // MEMOs
  const draftsSavedMemo = useMemo(() => draftsSaved, [draftsSaved]);
  const callIdMemo = useMemo(() => callId, [callId]);

  // CONSTs
  const id = callIdMemo && (callIdMemo?.raw || callIdMemo);
  const account = auth && auth.account_id;
  const userName = auth && auth.user_name;

  // EFFECTS
  useEffect(() => {
    if (draftsSavedMemo && id) {
      const find = draftsSavedMemo.filter(e => e.id === id);
      if (find.length !== 0) {
        setNote(find[0].note);
      }
    }

    return () => {
      setNote('');
    };
  }, [draftsSavedMemo, setNote, id]);

  useEffect(() => {
    if (minWords) setMinWordsRequired(minWords);
  }, [minWords]);

  async function onSubmit() {
    dispatch(actions.saveNoteByCallId(id, note));

    dispatch(actions.removeDraft(id, account));
    lib.ToastMessage('success', messagesToast().CALLS.notes.new.success);

    setNote('');
    setMinWordsRequired(minWords);
    onClose(false);
  }

  function autoSaveDraft(text) {
    const missingWordsLen = countWords(text) >= minWords;
    setNote(text);
    setEnableSubmitBtn(missingWordsLen);

    if (text) {
      if (error) {
        setError(null);
      }

      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = setTimeout(() => {
        dispatch(
          actions.saveDraft({
            id,
            note: text,
            account,
            userName,
          })
        );
        setMessage(true);

        setTimeout(() => {
          setMessage(false);
        }, 3000);
      }, 500);
    } else {
      setNote('');
      setMinWordsRequired(minWords);
    }
  }

  function cancelDraft() {
    if (error) {
      setError(null);
    }
    dispatch(actions.removeDraft(id, account));
    setNote('');
    onClose(false);
  }

  function renderTitle() {
    if (minWordsRequired > 0) {
      return (
        <span>
          {t('components.sendNotes.title')} (
          {t('components.sendNotes.atLeastWords', {
            minWordsRequired,
          })}
          )
        </span>
      );
    }

    return <span>{t('components.sendNotes.title')}</span>;
  }

  return (
    <>
      {open && (
        <Container>
          <div
            className="title-notes"
            style={{
              flex: 1,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            {renderTitle()}
            <Close className="close-icon" onClick={() => onClose(false)} />
          </div>
          <BoxPostNote>
            <TextField
              id="outlined-dense-multiline"
              margin="dense"
              variant="outlined"
              multiline
              autoFocus
              rows={2}
              onChange={text => autoSaveDraft(text.target.value)}
              maxRows="4"
              value={note}
              className={error ? 'error-input' : 'normal-input'}
            />
            <div className="row-controls">
              <div className="box-messages">
                {message && (
                  <span className="draftMessage">
                    {t('components.sendNotes.draft')}
                  </span>
                )}
                {error && <span className="errorMessage">{error}</span>}
              </div>
              <div className="groupBtns">
                <div
                  className="btnControls off"
                  onClick={() => cancelDraft()}
                  role="presentation">
                  <span className="count">
                    {t('components.sendNotes.cancel')}
                  </span>
                </div>

                <div
                  className={`btnControls ${
                    enableSubmitBtn ? 'on' : 'disabled'
                  }`}
                  disabled={!enableSubmitBtn}
                  onClick={enableSubmitBtn ? () => onSubmit() : () => false}
                  role="presentation">
                  <span className="count">
                    {t('components.sendNotes.save')}
                  </span>
                </div>
              </div>
            </div>
          </BoxPostNote>
        </Container>
      )}
    </>
  );
}

SendNotes.defaultProps = {
  minWords: 1,
};

SendNotes.propTypes = {
  minWords: lib.PropTypes.number,
  callId: lib.PropTypes.string.isRequired,
  open: lib.PropTypes.bool.isRequired,
  onClose: lib.PropTypes.func.isRequired,
};
